import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  accessToken,
  apiEndpoints,
  authToken,
  customBranding,
} from "../../config/config";
import image from "../../assets/user.png";
import NewChat from "./NewChat";
import { format } from "date-fns";
import VoiceBotFooter from "../Layout/VoiceBotFooter";
import { useStaticTranslations } from "../../utils/staticTranslations"; 
import { useLanguage } from "../../contexts/LanguageContext";

const containerVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  initial: { y: 20, opacity: 0 },
  animate: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const Participants = ({ onSelectParticipant, onSelectExistingChat }) => {
  const [participants, setParticipants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const isExpandedWindow = window.opener != null;
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedParticipantId, setSelectedParticipantId] = useState(null);
  const { currentLanguage } = useLanguage(); 
  const { buttonLabel } = useStaticTranslations(currentLanguage.code); 


  useEffect(() => {
    // Fetch participants data here
    const fetchParticipants = async () => {
      try {
        const response = await axios.get(apiEndpoints.participants, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
            clientToken: `Bearer ${accessToken}`,
          },
        });
        setParticipants(response.data);
        setFilteredParticipants(response.data);
        console.log("Participants:", response.data);
      } catch (error) {
        console.error("Error fetching participants:", error);
      }
    };

    fetchParticipants();
  }, []);

  // Filter participants based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredParticipants(participants);
    } else {
      const filtered = participants.filter((participant) =>
        participant.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredParticipants(filtered);
    }
  }, [searchQuery, participants]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleParticipantClick = async (participant) => {
    const userId = localStorage.getItem("userId");
    const chatBotId = localStorage.getItem("chatBotId");

    // Toggle functionality for expanded window
    if (isExpandedWindow && selectedParticipantId === participant.id) {
      // If clicking the same participant, close the chat
      setSelectedConversationId(null);
      setSelectedParticipantId(null);
      return;
    }

    // Set the selected participant ID
    setSelectedParticipantId(participant.id);

    try {
      const response = await axios.post(
        apiEndpoints.findOrCreateConversation,
        {
          user_id: userId,
          other_user_id: participant.id,
          chatBotId: chatBotId,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
            clientToken: `Bearer ${accessToken}`,
          },
        }
      );

      console.log("Conversation created or found:", response.data);

      // Check the status from the response
      if (response.data.status === "existing") {
        // For existing conversations
        if (isExpandedWindow) {
          setSelectedConversationId(response.data.conversation_id);
        } else {
          onSelectExistingChat(response.data.conversation_id);
        }
      } else {
        // For new conversations
        if (isExpandedWindow) {
          setSelectedConversationId(response.data.conversation_id);
        } else {
          onSelectParticipant(participant);
        }
      }
    } catch (error) {
      console.error("Error creating or finding conversation:", error);
      // In case of error, still try to continue with the default flow
      if (!isExpandedWindow) {
        onSelectParticipant(participant);
      }
    }
  };

  const handleBackToParticipants = () => {
    setSelectedConversationId(null);
    setSelectedParticipantId(null);
  };

  // Render different layouts based on window mode
  if (!isExpandedWindow) {
    // Original compact view for embedded window
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <div className="flex-grow overflow-y-auto p-4 pb-[5rem]">
          <div className="flex-grow mb-4">
            <input
              type="text"
              placeholder={buttonLabel('searchParticipants')}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <motion.div
            variants={containerVariants}
            initial="initial"
            animate="animate"
            className="space-y-3"
          >
            {filteredParticipants.map((participant) => (
              <motion.div
                key={participant.id}
                variants={itemVariants}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4"
              >
                <div
                  className="cursor-pointer flex items-center space-x-4"
                  onClick={() => {
                    handleParticipantClick(participant);
                  }}
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                    <img
                      src={participant.image || image}
                      alt={participant.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-grow">
                    <span className="text-sm font-medium text-gray-800">
                      {participant.name}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    );
  } else {
    // WhatsApp-like expanded view with left sidebar and right content area
    return (
      <div className="flex h-screen min-h-0">
        {/* Left sidebar - Participants list */}
        <div className="w-1/3 border-r flex flex-col bg-white h-full min-h-0">
          <div className="p-4 border-b">
            <input
              type="text"
              placeholder={buttonLabel('searchParticipants')}
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <div className="flex-grow overflow-y-auto min-h-0">
            <motion.div
              variants={containerVariants}
              initial="initial"
              animate="animate"
              className="space-y-0"
            >
              {filteredParticipants.map((participant) => (
                <motion.div
                  key={participant.id}
                  variants={itemVariants}
                  className={`border-b hover:bg-gray-50 transition-colors p-3 cursor-pointer ${
                    selectedParticipantId === participant.id
                      ? "!bg-gray-100 rounded-md border-1 border-[#2888b5]"
                      : ""
                  }`}
                  onClick={() => {
                    handleParticipantClick(participant);
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                      <img
                        src={participant.image || image}
                        alt={participant.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-grow">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-800">
                          {participant.name}
                        </span>
                        <span className="text-xs text-gray-500">
                        {participant?.updated_at
                          ? format(
                              new Date(participant?.updated_at),
                              "HH:mm"
                            )
                          : "N/A"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {selectedParticipantId === participant.id
                          ? buttonLabel('closeConversation')
                          : buttonLabel('startConversation')}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Right content area - Chat or Logo */}
        <div className="w-2/3 flex flex-col h-full min-h-0 bg-gray-50">
          <div className="flex flex-col flex-grow h-full min-h-0">
            {selectedConversationId ? (
              <NewChat
                conversationId={selectedConversationId}
                onBack={handleBackToParticipants}
                isInChatbot={true}
              />
            ) : (
              <div className="flex flex-col items-center justify-center flex-grow h-full min-h-0">
                <div className="w-48 h-48 mb-4">
                  <img
                    src={customBranding.logo_url}
                    alt="Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h2 className="text-xl text-gray-600 font-light">
                  Select a conversation to start chatting
                </h2>
              </div>
            )}
          </div>
          {/* Footer always at the bottom in expanded window */}
          <VoiceBotFooter config={customBranding} />
        </div>
      </div>
    );
  }
};

export default Participants;
